import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import ScrollToTop from './components/ScrollToTop';
import PasswordProtection from './components/common/PasswordProtection';

// Loading Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-boutique-cream">
    <div className="w-12 h-12 border-[3px] border-boutique-rose-light border-t-boutique-rose rounded-full animate-spin" />
  </div>
);

// Customer Pages - Lazy Loaded
const CustomerLayout = lazy(() => import('./pages/customer/CustomerLayout'));
const HomePage = lazy(() => import('./pages/customer/HomePage'));
const CatalogPage = lazy(() => import('./pages/customer/CatalogPage'));
const CategoriesPage = lazy(() => import('./pages/customer/CategoriesPage'));
const FlowerDetailPage = lazy(() => import('./pages/customer/FlowerDetailPage'));
const CartPage = lazy(() => import('./pages/customer/CartPage'));
const CheckoutPage = lazy(() => import('./pages/customer/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('./pages/customer/OrderSuccessPage'));
const OrderTrackingPage = lazy(() => import('./pages/customer/OrderTrackingPage'));
const OrderLookupPage = lazy(() => import('./pages/customer/OrderLookupPage'));
const ShippingPolicyPage = lazy(() => import('./pages/customer/ShippingPolicyPage'));
const AboutUsPage = lazy(() => import('./pages/customer/AboutUsPage'));
const TermsConditionsPage = lazy(() => import('./pages/customer/TermsConditionsPage'));
const RefundPolicyPage = lazy(() => import('./pages/customer/RefundPolicyPage'));
const ContactUsPage = lazy(() => import('./pages/customer/ContactUsPage'));
const CustomizeBouquetPage = lazy(() => import('./pages/customer/CustomizeBouquetPage'));

// Admin Pages - Lazy Loaded
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminFlowers = lazy(() => import('./pages/admin/AdminFlowers'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminTags = lazy(() => import('./pages/admin/AdminTags'));
const AdminDeliveryZones = lazy(() => import('./pages/admin/AdminDeliveryZones'));
const AdminDeliverySlots = lazy(() => import('./pages/admin/AdminDeliverySlots'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCreateOrder = lazy(() => import('./pages/admin/AdminCreateOrder'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminBulkImport = lazy(() => import('./pages/admin/AdminBulkImport'));

function App() {
  return (
    <PasswordProtection>
      <Router>
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Customer Routes */}
              <Route path="/" element={<CustomerLayout />}>
                <Route index element={<HomePage />} />
                <Route path="catalog" element={<CatalogPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="flower/:id" element={<FlowerDetailPage />} />
                <Route path="cart" element={<CartPage />} />
                <Route path="checkout" element={<CheckoutPage />} />
                <Route path="order-success" element={<OrderSuccessPage />} />
                <Route path="order-tracking" element={<OrderTrackingPage />} />
                <Route path="track-order" element={<OrderLookupPage />} />
                <Route path="shipping-policy" element={<ShippingPolicyPage />} />
                <Route path="about-us" element={<AboutUsPage />} />
                <Route path="terms-conditions" element={<TermsConditionsPage />} />
                <Route path="refund-policy" element={<RefundPolicyPage />} />
                <Route path="contact-us" element={<ContactUsPage />} />
                <Route path="customize" element={<CustomizeBouquetPage />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/*" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="flowers" element={<AdminFlowers />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="tags" element={<AdminTags />} />
                <Route path="bulk-import" element={<AdminBulkImport />} />
                <Route path="delivery-zones" element={<AdminDeliveryZones />} />
                <Route path="delivery-slots" element={<AdminDeliverySlots />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="create-order" element={<AdminCreateOrder />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </Router>
    </PasswordProtection>
  );
}

export default App;
